import { sumBy } from "lodash";

/** A water class's figures as the API returns them, in litres. */
export type VolumeSource = {
  balance: number | string;
  usage: number | string;
  overusedBalance?: number | string;
  /** Net of the period's approved transfers in and out. */
  transfer?: number | string;
};

/** The figures every Zenith page shows; GLOSSARY.md defines them. */
export type Volumes = {
  allocated: number;
  used: number;
  remaining: number;
  traded: number;
};

/**
 * Allocated = balance + usage; Used = usage + overused balance;
 * Remaining = balance − overused balance. So Remaining + Used = Allocated.
 */
export const volumesOf = ({
  balance,
  usage,
  overusedBalance = 0,
  transfer = 0,
}: VolumeSource): Volumes => ({
  allocated: +balance + +usage,
  used: +usage + +overusedBalance,
  remaining: +balance - +overusedBalance,
  traded: +transfer,
});

export const sumVolumes = (list: Volumes[]): Volumes => ({
  allocated: sumBy(list, "allocated"),
  used: sumBy(list, "used"),
  remaining: sumBy(list, "remaining"),
  traded: sumBy(list, "traded"),
});

/** A zone's volumes: the sum of its water classes'. */
export const zoneVolumes = (zone: { waterClasses?: VolumeSource[] }): Volumes =>
  sumVolumes((zone.waterClasses ?? []).map(volumesOf));

/** A water class's allocation as the API itemises it, in litres. */
export type AllocationSource = VolumeSource & {
  /** Drawn forward this period from the next period's allocation. */
  forwardDrawVolume?: number | string;
  /** Drawn forward last period from this period's allocation. */
  forwardDrawVolumeOfLastAccountingPeriod?: number | string;
  /** The announced share, a fraction of entitlement volume: 0.5 is 50%. */
  rate?: number | string;
  hasActiveRate?: boolean;
};

/**
 * A water class's allocation item by item, in litres, as its card lists it:
 * the announced volume, plus approved transfers and this period's forward
 * draw, less the repayment of last period's forward draw, makes `available`,
 * which is Allocated. GLOSSARY.md defines the announced volume.
 */
export const allocationOf = (waterClass: AllocationSource) => {
  const { allocated, traded } = volumesOf(waterClass);
  const forwardDraw = +(waterClass.forwardDrawVolume ?? 0);
  // At the share rounded to a whole percent, as Polestar's WaterClassSummary
  // repays it; change both together.
  const repaid = waterClass.hasActiveRate
    ? (+(waterClass.forwardDrawVolumeOfLastAccountingPeriod ?? 0) *
        Math.round(+(waterClass.rate ?? 0) * 100)) /
      100
    : 0;

  return {
    announced: allocated - traded - forwardDraw + repaid,
    traded,
    forwardDraw,
    repaid,
    available: allocated,
  };
};

/** A reading's ledger bookings, in litres, each a list of `{ [key]: volume }`. */
export type ClassProportions = {
  /** By zone identifier: the reading's whole volume. */
  proportions?: Record<string, number | string>[];
  /** By water class id: the volume taken within each class's balance. */
  waterClassProportions?: Record<string, number | string>[];
  /** By water class id: which classes the volume beyond the balance went to. */
  overusedWaterClassProportions?: Record<string, number | string>[];
};

const total = (list: Record<string, number | string>[] = []) =>
  sumBy(list.flatMap(Object.values), Number);

/**
 * A reading's Used by water class id, in litres: each class's usage plus the
 * overuse booked to it, so the classes total the reading's zone volume.
 * The overuse is the zone volume less the classes' usage. It splits across the
 * classes `overusedWaterClassProportions` names in proportion to their volumes
 * there, whose sizes can't be trusted: the API's offsetOverused saves one
 * running total across readings into each. Overuse naming no class is keyed "".
 */
export const usedByWaterClass = ({
  proportions,
  waterClassProportions = [],
  overusedWaterClassProportions = [],
}: ClassProportions): Record<string, number> => {
  const used: Record<string, number> = {};
  const add = (key: string, volume: number) => {
    used[key] = (used[key] ?? 0) + volume;
  };
  for (const [key, volume] of waterClassProportions.flatMap(Object.entries))
    add(key, Number(volume));

  const overuse = total(proportions) - total(waterClassProportions);
  if (!overuse) return used;
  const shares = overusedWaterClassProportions
    .flatMap(Object.entries)
    .filter(([, volume]) => Number(volume) > 0);
  const weight = sumBy(shares, ([, volume]) => Number(volume));
  if (!weight) add("", overuse);
  for (const [key, volume] of shares)
    add(key, (overuse * Number(volume)) / weight);
  return used;
};
